import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  exportAuditLogList,
  fetchAuditLogFilters,
  fetchAuditLogList,
} from '../api/auditLogsApi'

const DEFAULT_PAGE_SIZE = 25

function resolveErrorMessage(requestError, fallbackMessage) {
  const detail = requestError?.response?.data?.detail
  if (Array.isArray(detail)) {
    const normalized = detail
      .map((item) => item?.msg || item?.message || '')
      .filter(Boolean)
      .join(', ')
    if (normalized) return normalized
  }
  if (detail && typeof detail === 'object') {
    const message = detail?.message || detail?.msg
    if (typeof message === 'string' && message.trim()) return message
  }
  if (typeof detail === 'string' && detail.trim()) return detail
  return fallbackMessage
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename || 'audit-log-export.xls'
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  window.URL.revokeObjectURL(url)
}

export function useAuditLogScreen() {
  const [searchParams, setSearchParams] = useSearchParams()
  const initialModule = searchParams.get('module') || ''
  const initialEntityId = searchParams.get('entityId') || ''
  const autoExpand = searchParams.get('autoExpand') === 'true' || searchParams.get('autoExpand') === '1'

  const [isLoading, setIsLoading] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [error, setError] = useState('')
  const [rows, setRows] = useState([])
  const [userOptions, setUserOptions] = useState([{ value: '', label: 'All Users' }])
  const [moduleOptions, setModuleOptions] = useState([{ value: '', label: 'All Modules' }])
  const [actionOptions, setActionOptions] = useState([{ value: '', label: 'All Actions' }])
  const [expandedRowId, setExpandedRowId] = useState(null)
  const [summary, setSummary] = useState({ totalRows: 0 })
  const [filters, setFilters] = useState({
    userId: '',
    module: initialModule,
    action: '',
    entityId: initialEntityId,
    dateFrom: '',
    dateTo: '',
  })
  const [page, setPage] = useState(1)

  const pageSize = DEFAULT_PAGE_SIZE

  const appliedFiltersPayload = useMemo(() => {
    return {
      userId: filters.userId ? Number(filters.userId) : undefined,
      module: filters.module || undefined,
      action: filters.action || undefined,
      entityId: filters.entityId ? Number(filters.entityId) : undefined,
      dateFrom: filters.dateFrom || undefined,
      dateTo: filters.dateTo || undefined,
      page,
      pageSize,
    }
  }, [filters.action, filters.dateFrom, filters.dateTo, filters.entityId, filters.module, filters.userId, page, pageSize])

  const totalCount = Number(summary?.totalRows || 0)
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))

  const runReport = useCallback(
    async (payloadOverride = null) => {
      setIsLoading(true)
      setError('')
      try {
        const result = await fetchAuditLogList(payloadOverride || appliedFiltersPayload)
        const items = Array.isArray(result?.items) ? result.items : []
        setRows(items)
        setSummary({ totalRows: Number(result?.totalCount || 0) })
        if (autoExpand && items.length > 0) {
          const firstRow = items[0]
          const firstRowId = String(firstRow?.id || `${firstRow?.log_time}-${firstRow?.actor_name}-${firstRow?.action}`)
          setExpandedRowId(firstRowId)
        }
      } catch (requestError) {
        setRows([])
        setSummary({ totalRows: 0 })
        setError(resolveErrorMessage(requestError, 'Failed to load audit logs.'))
      } finally {
        setIsLoading(false)
      }
    },
    [appliedFiltersPayload, autoExpand],
  )

  const hydrateOptions = useCallback(async () => {
    const data = await fetchAuditLogFilters()
    setUserOptions([
      { value: '', label: 'All Users' },
      ...((data?.users || []).map((item) => ({ value: String(item.id), label: item.name }))),
    ])
    setModuleOptions([
      { value: '', label: 'All Modules' },
      ...((data?.modules || []).map((item) => ({
        value: String(item.value || ''),
        label: String(item.label || item.value || ''),
      }))),
    ])
    setActionOptions([
      { value: '', label: 'All Actions' },
      ...((data?.actions || []).map((item) => ({
        value: String(item.value || ''),
        label: String(item.label || item.value || ''),
      }))),
    ])
  }, [])

  useEffect(() => {
    let active = true
    async function load() {
      try {
        const data = await fetchAuditLogFilters()
        if (!active) return
        setUserOptions([
          { value: '', label: 'All Users' },
          ...((data?.users || []).map((item) => ({ value: String(item.id), label: item.name }))),
        ])
        setModuleOptions([
          { value: '', label: 'All Modules' },
          ...((data?.modules || []).map((item) => ({
            value: String(item.value || ''),
            label: String(item.label || item.value || ''),
          }))),
        ])
        setActionOptions([
          { value: '', label: 'All Actions' },
          ...((data?.actions || []).map((item) => ({
            value: String(item.value || ''),
            label: String(item.label || item.value || ''),
          }))),
        ])
      } catch (requestError) {
        if (!active) return
        setError(resolveErrorMessage(requestError, 'Failed to load audit options.'))
      }
    }

    load()

    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    runReport()
  }, [runReport])

  const onFilterChange = useCallback((key, value) => {
    setExpandedRowId(null)
    setPage(1)
    setFilters((prev) => ({ ...prev, [key]: value }))
  }, [])

  const refresh = useCallback(async () => {
    setExpandedRowId(null)
    setPage(1)
    setError('')
    try {
      await hydrateOptions()
    } catch (requestError) {
      setError(resolveErrorMessage(requestError, 'Failed to refresh audit options.'))
    }
    await runReport({
      ...appliedFiltersPayload,
      page: 1,
    })
  }, [appliedFiltersPayload, hydrateOptions, runReport])

  const exportCurrent = useCallback(async () => {
    setIsExporting(true)
    try {
      const { blob, filename } = await exportAuditLogList(appliedFiltersPayload)
      downloadBlob(blob, filename)
    } catch (requestError) {
      setError(resolveErrorMessage(requestError, 'Failed to export audit logs.'))
    } finally {
      setIsExporting(false)
    }
  }, [appliedFiltersPayload])

  const showRangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1
  const showRangeEnd = totalCount === 0 ? 0 : Math.min(page * pageSize, totalCount)

  return {
    state: {
      isLoading,
      isExporting,
      error,
      rows,
      userOptions,
      moduleOptions,
      actionOptions,
      filters,
      expandedRowId,
      page,
      pageSize,
      totalPages,
      totalCount,
      showRangeStart,
      showRangeEnd,
    },
    actions: {
      setFilter: onFilterChange,
      refresh,
      exportCurrent,
      setPage: (nextPage) => {
        const normalized = Math.max(1, Math.min(totalPages, Number(nextPage) || 1))
        setExpandedRowId(null)
        setPage(normalized)
      },
      toggleExpandedRow: (rowId) => {
        setExpandedRowId((prev) => (prev === rowId ? null : rowId))
      },
      clearEntityFilter: () => {
        setSearchParams({})
        setFilters((prev) => ({ ...prev, entityId: '', module: '' }))
        setPage(1)
        setExpandedRowId(null)
      },
    },
  }
}
